import { A } from '@adornis/base/env-info.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import type { ProductAspect } from '@chancenwerk/master-data-management/kpf/entities/aspects/ProductAspect.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import type { PartnerActiveAspect } from '@chancenwerk/master-data-management/partner/entities/aspects/PartnerActiveAspect.js';
import type { PartnerAspect } from '@chancenwerk/master-data-management/partner/entities/aspects/PartnerAspect.js';
import type { ChancenwerkUserAspect } from '@chancenwerk/master-data-management/users/entities/aspects/ChancenwerkUserAspect.js';
import { DateTime } from 'luxon';
import { from, switchMap } from 'rxjs';

export enum PartnerActiveState {
  YES = 'ja',
  NO = 'nein',
  ACQUISITION = 'noch nur in Akquise',
}

@Entity()
export class PartnerFilter extends AdornisFilter {
  static override _class = 'PartnerFilter';

  constructor(doc: PartialEntityData<PartnerFilter>) {
    super(doc);
  }

  // Only used to check if a filter has been changed to a new one
  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(type => [String])
  public ids: Maybe<string[]>;

  @Field(type => [String])
  public partnerClass: Maybe<string[]>;

  protected getFilterArray() {
    const filter = this.toObject();

    // Wen want to have full kuerzel search. Even on products so we just ignore the product
    let kuerzelSchool = '';
    if (filter.search) {
      kuerzelSchool = filter.search.split('_').slice(0, 2).join('_');
    }

    const filterClass = filter.partnerClass?.length ? { _class: { $in: filter.partnerClass } } : {};
    const filterIds = filter.ids?.length
      ? {
          _id: {
            $in: filter.ids,
          },
        }
      : {};

    const filterName = filter.search
      ? {
          $or: [
            { name: { $regex: this._safeRegex(filter.search), $options: 'i' } },
            { kuerzel: { $regex: this._safeRegex(kuerzelSchool), $options: 'i' } },
            { addressFullText: { $regex: this._safeRegex(filter.search), $options: 'i' } },
          ],
        }
      : {};

    return [filterClass, filterIds, filterName];
  }

  override getMongoFilter(): Dictionary<any> {
    const filters = this.getFilterArray();
    const extendedFilter = super.getMongoFilter();
    if (extendedFilter?.$and?.length) filters.push(extendedFilter);
    if (!filters.length) return {};
    return { $and: filters };
  }
}

export const searchPartnerAggregation = (filter: PartnerFilter) => {
  let activeMatch = {};
  let activeKPFMatch = {};

  const activeAspect = filter.getFilterAspect<PartnerActiveAspect>('partnerActiveState');
  const connectedAspect = filter.getFilterAspect<ChancenwerkUserAspect>('partnerConnectedPerson');
  const fundingPartner = filter.getFilterAspect<PartnerAspect>('partnerFunding');
  const productAspect = filter.getFilterAspect<ProductAspect>('productStrings');

  const filterSocialIndicatorsAspect = filter.getFilterAspect<IntAspect>('socialIndicatorSchema');

  switch (activeAspect?.value) {
    case PartnerActiveState.YES:
      activeMatch = {
        $or: [
          { 'kpfs._id': { $exists: true } },
          {
            $and: [
              {
                $or: [
                  { endDate: { $exists: false } },
                  { endDate: null },
                  { endDate: undefined },
                  { endDate: { $gt: new Date() } },
                ],
              },
              {
                startDate: { $lte: new Date() },
              },
            ],
          },
        ],
      };

      // * If we search for active partners we want to have all active kpfs,
      // * because a partner can be active if at least one kpf is active
      activeKPFMatch = {
        startDatum: { $lte: new Date() },
        $or: [
          { endDatum: { $exists: false } },
          { endDatum: null },
          { endDatum: undefined },
          { endDatum: { $gt: new Date() } },
        ],
      };
      break;
    case PartnerActiveState.NO:
      activeMatch = { endDate: { $lt: DateTime.now().toJSDate() } };
      break;
    case PartnerActiveState.ACQUISITION:
      activeMatch = {
        _class: { $in: ['ChancenSchule', 'School'] },
        $and: [
          {
            $nor: [
              { 'kpfs._id': { $exists: true } },
              {
                $and: [
                  {
                    $or: [
                      { endDate: { $exists: false } },
                      { endDate: null },
                      { endDate: undefined },
                      { endDate: { $gt: new Date() } },
                    ],
                  },
                  {
                    startDate: { $lte: new Date() },
                  },
                ],
              },
            ],
          },
        ],
      };

      activeKPFMatch = {
        startDatum: { $exists: true },
      };
      break;
  }

  return [
    ...(activeAspect?.value || connectedAspect?.value || fundingPartner?.value?.length || productAspect?.values?.length
      ? [
          {
            $lookup: {
              from: 'kpfs',
              let: { partnerID: '$_id' },
              pipeline: [
                {
                  $match: {
                    $expr: { $eq: ['$schoolID', '$$partnerID'] },
                    ...activeKPFMatch,
                  },
                },
              ],
              as: 'kpfs',
            },
          },
          {
            $unwind: {
              path: '$kpfs',
              preserveNullAndEmptyArrays: true,
            },
          },
        ]
      : []),
    {
      $match: activeMatch,
    },
    ...(fundingPartner?.value?.length
      ? [
          {
            $match: {
              'kpfs.ragPartnerInformation.ragPartnerIsID': { $in: fundingPartner?.value },
            },
          },
        ]
      : []),
    ...(productAspect?.values?.length
      ? [
          {
            $match: {
              'kpfs.produkt._class': { $in: productAspect?.values },
            },
          },
        ]
      : []),
    ...(connectedAspect?.value
      ? [
          {
            $lookup: {
              from: SemanticRelationship._collectionName,
              let: { kpfIDs: '$kpfs._id', partnerID: '$_id' },
              pipeline: [
                {
                  $match: {
                    $expr: { $or: [{ $eq: ['$objectID', '$$kpfIDs'] }, { $eq: ['$objectID', '$$partnerID'] }] },
                    $and: [
                      connectedAspect?.value
                        ? {
                            subjectID: connectedAspect?.value,
                          }
                        : {},
                    ],
                  },
                },
              ],
              as: 'relations',
            },
          },
          {
            $match: {
              $or: [
                connectedAspect?.value
                  ? {
                      $or: [{ relations: { $not: { $size: 0 } } }],
                    }
                  : {},
              ],
            },
          },
        ]
      : []),
    {
      $addFields: {
        addressFullText: {
          $concat: ['$address.zipCity.zip', '$address.zipCity.city', ', ', '$address.strasse'],
        },
      },
    },
    ...(filterSocialIndicatorsAspect &&
    (filterSocialIndicatorsAspect?.max !== null || filterSocialIndicatorsAspect?.min !== null)
      ? [
          {
            $addFields: {
              indicatorsSort: {
                $sortArray: {
                  input: '$socialIndicators',
                  sortBy: { value: -1 },
                },
              },
            },
          },
          {
            $addFields: {
              latestObject: {
                $arrayElemAt: ['$indicatorsSort', 0],
              },
            },
          },
          {
            $match: {
              'latestObject.value': {
                $gte: filterSocialIndicatorsAspect?.min ?? 0,
                $lte: filterSocialIndicatorsAspect?.max ?? 9999, // arbitrary high number. Socialindices will not go this high
              },
            },
          },
        ]
      : []),

    {
      $match: filter.getMongoFilter(),
    },

    // dedupe ?
    {
      $group: {
        _id: '$_id',
        doc: { $first: '$$ROOT' },
      },
    },
    {
      $replaceRoot: { newRoot: '$doc' },
    },
  ];
};

const resolveSubscribePartnerByFilter =
  (skip: number, limit: number, filter: PartnerFilter) => (gqlFields: BaseQLSelectionSet<Partner>) => {
    return subscribePaginatedAggregation(Partner, skip, limit, {}, [
      ...searchPartnerAggregation(filter),
      { $sort: filter.getMongoOptions().sort },
    ]);
  };

const resolveSubscribeCountPartnerByFilter = (filter: PartnerFilter) => () => {
  const handle = getCollectionHandle<Partner>(Partner._collectionName);
  return from(publishFilter(Partner._class)).pipe(
    switchMap(publishFilter => {
      return handle.watchQuery({}, { projection: { _id: 1 } });
    }),
    switchMap(async docs => {
      const collection = await getRawCollection<Partner>(Partner._collectionName);
      const result = await collection.aggregate([...searchPartnerAggregation(filter), { $count: 'count' }]).toArray();

      return result[0]?.count ?? 0;
    }),
  );
};

export const subscribePartnerByFilter = registerSubscription({
  type: () => [Partner],
  operationName: 'subscribePartnerByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => PartnerFilter, name: 'filter' },
  ],
  resolve: resolveSubscribePartnerByFilter,
});

export const subscribeCountPartnerByFilter = registerSubscription({
  type: () => Int,
  operationName: 'subscribeCountPartnerByFilter',
  params: [{ type: () => PartnerFilter, name: 'filter' }],
  resolve: resolveSubscribeCountPartnerByFilter,
});
